import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';

interface SettingsPanelProps {
  gap: number;
  borderRadius: number;
  canvasBorder: number;
  cellBorder: number;
  borderColor: string;
  onGapChange: (value: number) => void;
  onBorderRadiusChange: (value: number) => void;
  onCanvasBorderChange: (value: number) => void;
  onCellBorderChange: (value: number) => void;
  onBorderColorChange: (value: string) => void;
}

const BORDER_COLORS = [
  '#ffffff', '#000000', '#374151', '#0ea5e9', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  gap,
  borderRadius,
  canvasBorder,
  cellBorder,
  borderColor,
  onGapChange,
  onBorderRadiusChange,
  onCanvasBorderChange,
  onCellBorderChange,
  onBorderColorChange,
}) => {
  const { t } = useLanguage();
  
  return (
    <div className="space-y-5">
      <h2 className="panel-title font-semibold text-foreground">{t.settings}</h2>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="panel-label text-muted-foreground">{t.cellGap}</Label>
            <span className="panel-value text-primary font-medium">{gap}px</span>
          </div>
          <Slider
            value={[gap]}
            onValueChange={([v]) => onGapChange(v)}
            min={0}
            max={40}
            step={2}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="panel-label text-muted-foreground">{t.cellRounding}</Label>
            <span className="panel-value text-primary font-medium">{borderRadius}px</span>
          </div>
          <Slider
            value={[borderRadius]}
            onValueChange={([v]) => onBorderRadiusChange(v)}
            min={0}
            max={50}
            step={2}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="panel-label text-muted-foreground">{t.canvasBorder}</Label>
            <span className="panel-value text-primary font-medium">{canvasBorder}px</span>
          </div>
          <Slider
            value={[canvasBorder]}
            onValueChange={([v]) => onCanvasBorderChange(v)}
            min={0}
            max={50}
            step={2}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="panel-label text-muted-foreground">{t.cellBorder}</Label>
            <span className="panel-value text-primary font-medium">{cellBorder}px</span>
          </div>
          <Slider
            value={[cellBorder]}
            onValueChange={([v]) => onCellBorderChange(v)}
            min={0}
            max={10}
            step={1}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label className="panel-label text-muted-foreground">{t.borderColor}</Label>
          <div className="flex gap-1.5 p-2 rounded-md bg-muted/50">
            {BORDER_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => onBorderColorChange(color)}
                aria-label={`Border color ${color}`}
                className={`w-6 h-6 rounded transition-all ${
                  borderColor === color
                    ? 'ring-2 ring-primary ring-offset-1 scale-110'
                    : 'ring-1 ring-border hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;